// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Attachment, Coding, Practitioner, PractitionerQualification } from '@medplum/fhirtypes';

// ─── FHIR System URLs ─────────────────────────────────────────────────────────

export const NPI_SYSTEM = 'http://hl7.org/fhir/sid/us-npi';
export const DEA_SYSTEM = 'http://hl7.org/fhir/sid/us-dea';
export const PTAN_SYSTEM = 'https://www.polarissleep.com/fhir/identifier/ptan';
export const TIMEZONE_EXTENSION = 'http://hl7.org/fhir/StructureDefinition/timezone';
export const ACCOUNT_TYPE_EXTENSION = 'https://www.polarissleep.com/fhir/extension/account-type';
export const LICENSE_SYSTEM = 'https://www.polarissleep.com/fhir/qualification/license';
export const APPLICATION_ID_SYSTEM = 'https://www.polarissleep.com/fhir/identifier/application-id';
export const PROVIDER_NPI_SYSTEM = 'https://www.polarissleep.com/fhir/identifier/provider-npi';
export const CREDENTIAL_SYSTEM = 'https://www.polarissleep.com/fhir/qualification/credential';
export const QUALIFICATION_TYPE_SYSTEM = 'https://www.polarissleep.com/fhir/qualification/type';

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getIdentifier(p: Practitioner, system: string): string {
  return p.identifier?.find((id) => id.system === system)?.value ?? '';
}

export function getTelecom(p: Practitioner, system: string): string {
  return p.telecom?.find((t) => t.system === system)?.value ?? '';
}

export function getExtensionValue(p: Practitioner, url: string): string {
  return p.extension?.find((e) => e.url === url)?.valueString ?? '';
}

export function getQualificationsBySystem(
  qualifications: PractitionerQualification[],
  system: string
): PractitionerQualification[] {
  return (qualifications ?? []).filter((q) => q.code?.coding?.[0]?.system === system);
}

export function getQualificationText(qualifications: PractitionerQualification[], code: string): string {
  return (qualifications ?? [])
    .filter((q) => q.code?.coding?.[0]?.system === QUALIFICATION_TYPE_SYSTEM && q.code?.coding?.[0]?.code === code)
    .map((q) => q.code?.text ?? '')
    .filter(Boolean)
    .join(', ');
}

export function getQualificationExtension(q: PractitionerQualification, url: string): string {
  return q.extension?.find((e) => e.url === url)?.valueString ?? '';
}

export function getQualificationFileAttachment(q: PractitionerQualification): Attachment | undefined {
  return q.extension?.find((e) => e.url === 'file')?.valueAttachment;
}

export function formatDate(dateStr?: string): string {
  if (!dateStr) {
    return '';
  }
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) {
    return dateStr;
  }
  return `${month}/${day}/${year}`;
}

// ─── Profile completion ───────────────────────────────────────────────────────

export type ProfileMissingRequirement = 'provider-information';

export const REQUIRED_PROVIDER_INFORMATION_FIELDS = [
  'title',
  'firstName',
  'lastName',
  'phone',
  'npi',
  'degree',
  'certification',
  'dea',
  'birthDate',
] as const;

export function hasCompleteProviderInformation(p: Practitioner): boolean {
  const name = p.name?.[0];
  if (!name?.prefix?.[0]?.trim()) {
    return false;
  }
  if (!name?.given?.[0]?.trim()) {
    return false;
  }
  if (!name?.family?.trim()) {
    return false;
  }
  if (!p.birthDate) {
    return false;
  }
  if (!getTelecom(p, 'phone')) {
    return false;
  }
  if (!getIdentifier(p, NPI_SYSTEM)) {
    return false;
  }
  if (!getIdentifier(p, DEA_SYSTEM)) {
    return false;
  }
  const quals = p.qualification ?? [];
  if (!getQualificationText(quals, 'degree')) {
    return false;
  }
  if (!getQualificationText(quals, 'certification')) {
    return false;
  }
  return true;
}

export function getMissingProfileRequirements(p: Practitioner): ProfileMissingRequirement[] {
  const missing: ProfileMissingRequirement[] = [];
  if (!hasCompleteProviderInformation(p)) {
    missing.push('provider-information');
  }
  return missing;
}

// ─── Onboarding lifecycle tag ─────────────────────────────────────────────────
// Applied to Practitioner.meta.tag while the provider holds the
// `provider-onboarding-access-policy`. Removed by the `onboarding-policy-swap`
// bot once the profile is complete and the membership has been upgraded.

export const ONBOARDING_TAG_SYSTEM = 'https://polarissleep.com/onboarding';
export const ONBOARDING_PENDING_CODE = 'pending';

export function isOnboardingPending(p: Practitioner): boolean {
  return p.meta?.tag?.some((t) => t.system === ONBOARDING_TAG_SYSTEM && t.code === ONBOARDING_PENDING_CODE) ?? false;
}

/**
 * Returns true iff the Practitioner carries the onboarding-pending tag.
 * Used by the send-provider-welcome-email bot and (Phase 3+) the staffing UI
 * to gate resend/cancel invite actions.
 *
 * Alias of `isOnboardingPending` with an explicit name for cross-phase clarity.
 */
export function isPractitionerOnboardingPending(p: Practitioner): boolean {
  return isOnboardingPending(p);
}

export function stripOnboardingPendingTag(tags: Coding[] | undefined): Coding[] | undefined {
  if (!tags) {
    return tags;
  }
  const filtered = tags.filter((t) => !(t.system === ONBOARDING_TAG_SYSTEM && t.code === ONBOARDING_PENDING_CODE));
  return filtered.length > 0 ? filtered : undefined;
}

export function withOnboardingPendingTag(tags: Coding[] | undefined): Coding[] {
  const existing = tags ?? [];
  if (existing.some((t) => t.system === ONBOARDING_TAG_SYSTEM && t.code === ONBOARDING_PENDING_CODE)) {
    return existing;
  }
  return [...existing, { system: ONBOARDING_TAG_SYSTEM, code: ONBOARDING_PENDING_CODE }];
}
